import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  DateRangePicker,
  type DateRangePreset,
  type DateRangeValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { ReportingRepository } from '~/repositories';
import { NotificationPropsType, useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { getDateFormat } from '~/util';

// Mirrors the API: reports start no earlier than 2022-01-01 and cover at most 93 days.
const EARLIEST_REPORT_DATE = new Date(2022, 0, 1);
const MAX_RANGE_DAYS = 93;

const REPORT_TYPES = [
  { label: 'Bank Account Summary', value: 'bank_account_summary' },
  { label: 'Bank Account Transaction', value: 'bank_account_transaction' },
];

interface ScheduleReportForm {
  type: string;
  dateRange: DateRangeValue;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const daysBefore = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() - days);

const getDateRangePresets = (today: Date): DateRangePreset[] => [
  { label: 'Today', range: { from: today, to: today } },
  { label: 'Last 3 Days', range: { from: daysBefore(today, 3), to: today } },
  { label: 'Last Week', range: { from: daysBefore(today, 7), to: today } },
  {
    label: 'Last Month',
    range: { from: new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()), to: today },
  },
];

const validateDateRange = ({ from: fromDate, to: toDate }: DateRangeValue) => {
  // The API needs both ends, so an empty range can't be scheduled either.
  if (!fromDate && !toDate) {
    return 'Select a date range';
  }
  if (!fromDate) {
    return 'Select a Start Date';
  }
  if (!toDate) {
    return 'Select an End Date';
  }
  if (fromDate > toDate) {
    return 'Start Date must be on or before End Date';
  }
  const lastAllowed = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate() + MAX_RANGE_DAYS - 1);
  return toDate <= lastAllowed || `Date range cannot exceed ${MAX_RANGE_DAYS} days`;
};

export const PageReportingSchedule: React.FC = () => {
  const currentPermission = useSessionStore((s) => s.currentPermission);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler();
  const navigate = useNavigate();
  const canWrite = currentPermission?.reporting === 'write';
  const today = startOfDay(new Date());
  const presets = getDateRangePresets(today);

  const formMethods = useForm<ScheduleReportForm>({
    defaultValues: {
      type: '',
      dateRange: { from: undefined, to: undefined },
    },
  });

  const { handleSubmit } = formMethods;

  const scheduleReport = useMutation({
    mutationFn: (data: ScheduleReportForm) =>
      ReportingRepository.schedule({
        type: data.type,
        fromDate: data.dateRange.from ? getDateFormat(data.dateRange.from) : undefined,
        toDate: data.dateRange.to ? getDateFormat(data.dateRange.to) : undefined,
      }),
    onSuccess: () => {
      addSuccessNotification({ content: 'Report scheduled' });
      navigate(ROUTE.REPORTING);
    },
    onError: (error: Error & { code?: string }) => {
      const notification: NotificationPropsType = { content: error.message };

      // The API returns this when the platform has no reporting time zone set.
      if (error.code === 'feature_not_enabled') {
        notification.timeout = 8000;
        notification.actionButton = {
          label: 'Platform Settings',
          onClick: () => navigate(ROUTE.PLATFORM_SETTINGS),
        };
      }

      addDangerNotification(notification);
    },
  });

  const handleSchedule = (data: ScheduleReportForm) => scheduleReport.mutate(data);

  const cancelButton = (
    <Button onClick={() => navigate(ROUTE.REPORTING)} variant="secondary" size="sm" type="button">
      Cancel
    </Button>
  );
  const scheduleButton = (
    <Button
      onClick={handleSubmit(handleSchedule, handleSubmitErrors)}
      disabled={!canWrite || scheduleReport.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus />
      {scheduleReport.isPending ? 'Scheduling…' : 'Schedule Report'}
    </Button>
  );

  return (
    <>
      <PageActions>
        {cancelButton}
        {scheduleButton}
      </PageActions>

      {!canWrite && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <p className="m-0 max-w-[64ch] px-(--page-margin) pt-6 text-sm text-muted-foreground">
        Create a report for a date range or single day. Reports that include the current business day will be completed
        at the end of the day.
      </p>

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleSchedule, handleSubmitErrors)}>
          {RenderFields<ScheduleReportForm>({
            sections: [
              {
                fields: [
                  {
                    id: 'type',
                    label: 'Type',
                    rules: { required: 'Select a report Type' },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <Select
                        value={String(value ?? '')}
                        onValueChange={onChange}
                        onOpenChange={(open) => !open && onBlur()}
                      >
                        <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
                          <SelectValue placeholder="Select a report type" />
                        </SelectTrigger>
                        <SelectContent>
                          {REPORT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} data-cy={`dropdown-option-${type.value}`}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ),
                  },
                  {
                    id: 'dateRange',
                    label: 'Date Range',
                    rules: { validate: (value) => validateDateRange(value as DateRangeValue) },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <DateRangePicker
                        value={value as DateRangeValue}
                        onValueChange={onChange}
                        onBlur={onBlur}
                        presets={presets}
                        maxDays={MAX_RANGE_DAYS}
                        disabledDays={[{ before: EARLIEST_REPORT_DATE }, { after: today }]}
                        aria-invalid={!!error}
                      />
                    ),
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <div className="flex items-start justify-between px-(--page-margin) py-6">
        {cancelButton}
        {scheduleButton}
      </div>
    </>
  );
};
